import qs from "query-string";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import {
  BOOKING_CALENDAR_URL_PATH,
  BOOKINGS_URL,
  BookingsRoutes,
  BookingsViewTypeEnum,
  editFormDataAF,
  EditFormMode,
  generateSpecificSearchPattern,
  RequestBodyModel,
  saveLastNavigation,
  starredDisplayModeAtomFamily,
  StarredDisplayModeEnum,
  tableSearchAF,
  usePrefixedRouting,
} from "@ryde/shared";

import { useCrudFormCleanup } from "../hooks";

export const useBookingsNavigation = () => {
  const { navigatePrefixed } = usePrefixedRouting();
  const location = useLocation();
  const setBookingDto = useSetRecoilState(
    editFormDataAF(["booking", EditFormMode.CrudRoute]),
  );
  const { resetEditFormPreviousData } = useCrudFormCleanup(
    "booking",
    EditFormMode.CrudRoute,
  );
  const isBookingView = useMemo(
    (): boolean => location.pathname.includes("/bookings"),
    [location.pathname],
  );

  const isCalendarBookingView = useMemo(
    (): boolean => location.pathname.includes(BOOKING_CALENDAR_URL_PATH),
    [location.pathname],
  );

  const isBookingEdit = useMemo(
    (): boolean =>
      location.pathname.includes("/bookings/edit/") ||
      location.pathname.includes("/bookings/calendar/edit/"),
    [location.pathname],
  );

  const navigateBookingsGrid = useEvent((keepQuery = true): void => {
    const queryString = isBookingView && keepQuery ? location.search || "" : "";
    navigatePrefixed(BookingsRoutes.SEARCH_GRID + queryString);
    saveLastNavigation({
      route: BOOKINGS_URL,
      path: BookingsRoutes.SEARCH_GRID,
    });
  });
  const bookingSearchGrid = isBookingView ? (location.search ?? "") : "";

  const navigateBookingsCalendar = useEvent((keepQuery = true): void => {
    const queryString = isBookingView && keepQuery ? location.search || "" : "";
    navigatePrefixed(BookingsRoutes.SEARCH_CALENDAR + queryString);
    saveLastNavigation({
      route: BOOKINGS_URL,
      path: BookingsRoutes.SEARCH_CALENDAR,
    });
  });

  const navigateBookingsGridOrCalendar = useEvent((keepQuery = true): void => {
    if (isCalendarBookingView) {
      navigateBookingsCalendar(keepQuery);
    } else {
      navigateBookingsGrid(keepQuery);
    }
  });

  const navigateFromEdit = useEvent((keepQuery = true): void => {
    if (isCalendarBookingView) {
      navigateBookingsCalendar(keepQuery);
    } else {
      navigateBookingsGrid(keepQuery);
    }
  });

  const navigateBookingsAdd = useEvent(
    (keepQuery = true, initialData?: RequestBodyModel): void => {
      resetEditFormPreviousData();

      if (initialData) {
        setBookingDto(initialData);
      }

      const queryString =
        isBookingView && keepQuery ? location.search || "" : "";
      navigatePrefixed(
        isCalendarBookingView
          ? BookingsRoutes.ADD_CALENDAR + queryString
          : BookingsRoutes.ADD + queryString,
      );
    },
  );

  const navigateBookingsEdit = useEvent(
    (id: number, keepQuery = true): void => {
      resetEditFormPreviousData();
      const queryString =
        isBookingView && keepQuery ? location.search || "" : "";
      navigatePrefixed(
        isCalendarBookingView
          ? `${BookingsRoutes.EDIT_BASE_CALENDAR}${id}${queryString}`
          : `${BookingsRoutes.EDIT_BASE}${id}${queryString}`,
      );
    },
  );

  const bookingsPageViewType = useEvent((): BookingsViewTypeEnum =>
    isCalendarBookingView
      ? BookingsViewTypeEnum.Calendar
      : BookingsViewTypeEnum.Grid,
  );

  const setTableSearch = useSetRecoilState(tableSearchAF("booking"));
  const setStarredDisplayMode = useSetRecoilState(
    starredDisplayModeAtomFamily("booking"),
  );

  const navigateBookingsGridMultiSelect = useEvent((keepQuery = true): void => {
    const queryString = isBookingView && keepQuery ? location.search || "" : "";
    navigatePrefixed(BookingsRoutes.SEARCH_GRID + queryString);
    saveLastNavigation({
      route: BOOKINGS_URL,
      path: BookingsRoutes.SEARCH_GRID,
    });
  });

  const getBookingSetIdsFiltersLink = useEvent(
    ({ bookingIds }: { bookingIds: number[] }): string => {
      const searchPattern = generateSpecificSearchPattern(bookingIds);
      const queryString = qs.stringify({ search: searchPattern });

      return `${BookingsRoutes.SEARCH_GRID}?${queryString}`;
    },
  );

  const navigateToBookingSetIdsFilters = useEvent(
    ({ bookingIds }: { bookingIds: number[] }): void => {
      setStarredDisplayMode(StarredDisplayModeEnum.ShowAllIncludeStarred);
      setTableSearch(generateSpecificSearchPattern(bookingIds));
      const link = getBookingSetIdsFiltersLink({ bookingIds });
      navigatePrefixed(link);
    },
  );

  return {
    isCalendarBookingView,
    isBookingView,
    isBookingEdit,
    bookingsPageViewType,
    navigateBookingsGrid,
    navigateBookingsCalendar,
    navigateBookingsGridOrCalendar,
    navigateBookingsAdd,
    navigateBookingsEdit,
    navigateFromEdit,
    bookingSearchGrid,
    navigateBookingsGridMultiSelect,
    getBookingSetIdsFiltersLink,
    navigateToBookingSetIdsFilters,
  };
};
